"use client"

import { useEffect, useRef } from "react"

type CountUpNumberProps = {
  value: number
  duration?: number
  className?: string
}

const formatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 })
const formatCount = (value: number) => formatter.format(Math.round(value))

const CountUpNumber = ({
  value,
  duration = 1400,
  className = "",
}: CountUpNumberProps) => {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const element = ref.current
    if (
      !element ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      duration <= 0
    )
      return

    let frame = 0
    let startedAt: number | undefined
    let previous = -1

    const tick = (now: number) => {
      startedAt ??= now
      const progress = Math.min((now - startedAt) / duration, 1)
      const current = Math.round(value * (1 - Math.pow(1 - progress, 3)))
      // Avoid DOM writes when consecutive frames display the same integer.
      if (current !== previous) {
        element.textContent = formatCount(current)
        previous = current
      }
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect()
          frame = requestAnimationFrame(tick)
        }
      },
      { threshold: 0.35 },
    )
    observer.observe(element)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
      element.textContent = formatCount(value)
    }
  }, [duration, value])

  return (
    <span ref={ref} className={`tabular-nums ${className}`.trim()}>
      {formatCount(value)}
    </span>
  )
}

export default CountUpNumber
